import {FC, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {Button, Form, Modal} from 'react-bootstrap'
import {format} from 'date-fns'
import {useToast} from '../../../../../_fds/layout/components/toast-messages'
import {createLaborTemplateFromSchedule} from '../../core/_requests'

const modalsRoot = document.getElementById('root-modals') || document.body

type SaveScheduleAsTemplateModalProps = {
  show: boolean
  onHide: () => void
  locationId: number | null
  // The week on the board, and the day inside it that is currently selected —
  // a daily template saves that day, so the dialog names it rather than making
  // the manager guess which one it means.
  weekStart: Date
  selectedDate: Date
  // Whether the board is actually showing ONE day. On the week grids
  // selectedDate is only the week's anchor, so offering to save "the selected
  // day" there would quietly mean "Sunday" whatever the manager was looking at.
  canSaveSingleDay: boolean
}

/**
 * "Save current schedule as template" — the read direction of Apply Template.
 *
 * The shifts are never sent from here: the backend reads them out of the
 * schedule itself, so what gets saved is what the week really holds, not what
 * the board happens to have rendered. All this collects is the name and the two
 * choices that change what the saved template means — its grain (one day or the
 * whole week) and whether the positions come along.
 */
const SaveScheduleAsTemplateModal: FC<SaveScheduleAsTemplateModalProps> = ({
  show,
  onHide,
  locationId,
  weekStart,
  selectedDate,
  canSaveSingleDay,
}) => {
  const {showToast} = useToast()

  const [name, setName] = useState('')
  const [templateType, setTemplateType] = useState<'daily' | 'weekly'>('weekly')
  const [includePositions, setIncludePositions] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!show) return
    setName('')
    setTemplateType('weekly')
    setIncludePositions(true)
    setSaving(false)
  }, [show])

  const dayLabel = format(selectedDate, 'EEE MMM d, yyyy')
  const weekLabel = format(weekStart, 'MMM d, yyyy')

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed || !locationId) return
    // Never send 'daily' from a view that has no day on screen, whatever the
    // radio last held.
    const type = canSaveSingleDay ? templateType : 'weekly'
    setSaving(true)
    try {
      const result = await createLaborTemplateFromSchedule({
        location_id: locationId,
        name: trimmed,
        template_type: type,
        // Exactly one target, matching the grain — the API rejects both.
        date: type === 'daily' ? format(selectedDate, 'yyyy-MM-dd') : undefined,
        week_start: type === 'weekly' ? format(weekStart, 'yyyy-MM-dd') : undefined,
        include_positions: includePositions,
      })
      showToast('Template Saved', result.message, 'success')
      onHide()
    } catch (error: any) {
      // 409 (name taken) and 422 (nothing to save) both come back with a
      // sentence worth showing verbatim — a generic failure toast would hide
      // the one thing the manager can act on.
      const detail = error?.response?.data?.detail
      showToast(
        'Error',
        typeof detail === 'string' ? detail : 'Unable to save the schedule as a template!',
        'danger'
      )
    }
    setSaving(false)
  }

  return createPortal(
    <Modal
      id='fds_modal_save_schedule_as_template'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-600px'
      show={show}
      onHide={onHide}
      backdrop={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>Save Schedule as Template</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className='mb-6'>
          <label className='form-label fw-bold'>Template name</label>
          <Form.Control
            type='text'
            autoFocus
            placeholder='e.g. Busy summer week'
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) handleSave()
            }}
          />
        </div>

        <div className='mb-6'>
          <label className='form-label fw-bold'>What to save</label>
          {canSaveSingleDay ? (
            <>
              <Form.Check
                type='radio'
                id='save-template-type-weekly'
                name='save-template-type'
                className='fs-7 form-check-custom form-check-solid mb-2'
                checked={templateType === 'weekly'}
                onChange={() => setTemplateType('weekly')}
                label={`The whole week of ${weekLabel}`}
              />
              <Form.Check
                type='radio'
                id='save-template-type-daily'
                name='save-template-type'
                className='fs-7 form-check-custom form-check-solid'
                checked={templateType === 'daily'}
                onChange={() => setTemplateType('daily')}
                label={`Just the day on screen — ${dayLabel}`}
              />
            </>
          ) : (
            // A week grid has no day on screen to save, so there is nothing to
            // choose between — stating the target beats a radio group of one.
            <>
              <div className='fs-7 fw-semibold'>The whole week of {weekLabel}</div>
              <div className='text-gray-600 fs-8 mt-1'>
                Open the Timeline view to save a single day.
              </div>
            </>
          )}
        </div>

        <div className='mb-2'>
          <label className='form-label fw-bold'>Positions</label>
          <Form.Check
            type='checkbox'
            id='save-template-include-positions'
            className='fs-7 form-check-custom form-check-solid'
            checked={includePositions}
            onChange={(e) => setIncludePositions(e.target.checked)}
            label='Keep each shift’s position'
          />
          <div className='text-gray-600 fs-8 mt-1'>
            {includePositions
              ? 'Applying the template recreates the shifts on the same positions.'
              : 'The template keeps only the shift times — the shape of the coverage, with no position on any shift.'}
          </div>
        </div>

        <div className='text-gray-500 fs-8 mt-4'>
          Employees are never saved. Applying a template creates unassigned shifts for you to staff.
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant='primary'
          disabled={!name.trim() || !locationId || saving}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save template'}
        </Button>
      </Modal.Footer>
    </Modal>,
    modalsRoot
  )
}

export {SaveScheduleAsTemplateModal}
